import { Box, IconButton, MenuItem, Select, Typography } from '@mui/material'

//const COLOR_LIVE = '#5CB85C' //'#6ddb4a'
const COLOR_LIVE = '#008000' //'#6ddb4a'

interface PaginationBarProps {
    page: number
    totalPages: number
    loading: boolean
    onPageChange: (page: number) => void
    showOrderBy?: boolean
    orderBy?: string
    onOrderByChange?: (value: string) => void
    liveCount?: number | null
}

export function PaginationBar({
    page,
    totalPages,
    loading,
    onPageChange,
    showOrderBy = false,
    orderBy = 'pd_desc',
    onOrderByChange,
    liveCount,
}: PaginationBarProps) {
    if (totalPages <= 1 && !showOrderBy) return null

    const arrows = totalPages > 1 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
                onClick={() => onPageChange(page - 1)}
                disabled={page === 1 || loading}
                sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1.5 }}
            >
                <Box component="span" sx={{ fontSize: '18px', lineHeight: 1, px: 0.5 }}>‹</Box>
            </IconButton>
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, textAlign: 'center' }}>
                Page {page} of {totalPages}
            </Typography>
            <IconButton
                onClick={() => onPageChange(page + 1)}
                disabled={loading || page >= totalPages}
                sx={{ border: '1px solid rgba(0,0,0,0.12)', borderRadius: 1.5 }}
            >
                <Box component="span" sx={{ fontSize: '18px', lineHeight: 1, px: 0.5 }}>›</Box>
            </IconButton>
        </Box>
    )

    const orderBySelect = showOrderBy && (
        <Select
            size="small"
            value={orderBy}
            onChange={e => onOrderByChange?.(e.target.value)}
            sx={{ fontSize: '13px', minWidth: 200 }}
        >
            <MenuItem value="pd_desc">Published: Newest first</MenuItem>
            <MenuItem value="pd_asc">Published: Oldest first</MenuItem>
            <MenuItem value="cd_desc">Deadline: Soonest first</MenuItem>
            <MenuItem value="cd_asc">Deadline: Latest first</MenuItem>
            <MenuItem value="vm_desc">Value: Highest first</MenuItem>
            <MenuItem value="vm_asc">Value: Lowest first</MenuItem>
        </Select>
    )

    const liveBadge = liveCount != null && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 0.6, borderRadius: 99, backgroundColor: `${COLOR_LIVE}18`, border: `1px solid ${COLOR_LIVE}55` }}>
            <Box sx={{ position: 'relative', width: 9, height: 9, flexShrink: 0 }}>
                <Box sx={{
                    position: 'absolute', inset: 0, borderRadius: '50%', backgroundColor: COLOR_LIVE,
                    '@keyframes livePulse': { '0%': { transform: 'scale(1)', opacity: 0.8 }, '70%': { transform: 'scale(2.4)', opacity: 0 }, '100%': { transform: 'scale(2.4)', opacity: 0 } },
                    animation: 'livePulse 1.8s ease-out infinite',
                }} />
                <Box sx={{ position: 'absolute', inset: 0, borderRadius: '50%', backgroundColor: COLOR_LIVE }} />
            </Box>
            <Typography variant="body2" sx={{ fontWeight: 700, color: COLOR_LIVE, letterSpacing: '0.2px' }}>
                {liveCount.toLocaleString()} live results
            </Typography>
        </Box>
    )

    if (showOrderBy) {
        return (
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', alignItems: 'center', gap: 1 }}>
                <Box>{liveBadge}</Box>
                {arrows || <Box />}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {orderBySelect}
                </Box>
            </Box>
        )
    }

    return (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            {arrows}
        </Box>
    )
}
